import React, {useState, useEffect, useMemo, useRef} from 'react'
import Select, {MenuListProps, components, MultiValue, ActionMeta, StylesConfig} from 'react-select'
import {Form} from 'react-bootstrap'
import {useI18n} from '../Utils/_helpers/useI18n'
import {useLazyGetPromoActionListQuery} from '../../services/cellcast.v2'
import {PromoAction} from '../../services/cellcast.v2.types'
import './Style/InputPromoAction.scss'
import {CreatePromoActionModal} from '../pages/UserPages/promo-action/components/CreatePromoActionModal'
import {MAX_PROMO_ACTION} from '../Utils/Constant/WalletCard'

interface PromoActionOption {
  value: string
  label: string
  color?: string
}

interface InputPromoActionProps {
  value: string[]
  onChange: (value: string[]) => void
  className?: string
  error?: string
  name?: string
  promoActionList?:(value: string[]) => void
}

const InputPromoAction: React.FC<InputPromoActionProps> = ({value, onChange, className, error, name, promoActionList}) => {
  const {t} = useI18n()
  const [getPromoActionList] = useLazyGetPromoActionListQuery()
  const [availablePromoActions, setAvailablePromoActions] = useState<PromoAction[]>([])
  const [openPromoPopup, setOpenPromoPopup] = useState(false)
  const previousLabelsRef = useRef<string[]>([])
  const [newlyCreatedPromoId, setNewlyCreatedPromoId] = useState<string | null>(null)
  const DEFAULT_QUERY = {
    page: 1, 
    limit: 100
  }
  useEffect(() => {
    let isMounted = true

    const loadPromoActions = async () => {
      try {
        // Sort by updatedAt desc to show newest first, then by name
        const response = await getPromoActionList(DEFAULT_QUERY).unwrap()
        if (isMounted && response?.data?.items) {
          setAvailablePromoActions(response.data.items)
        }
      } catch (error) {
        if (isMounted) {
          console.error('Failed to load promo actions:', error)
        }
      }
    }

    loadPromoActions()

    // Cleanup function
    return () => {
      isMounted = false
    }
  }, [getPromoActionList])

  const promoOptions = useMemo(() => {
    return availablePromoActions.map((promo) => ({
      value: promo.id || '',
      label: promo.name || '',
      rawContactCount: promo.rawContactCount || 0,
    }))
  }, [availablePromoActions])

  const selectedOptions = useMemo(() => {
    return promoOptions.filter((option) => value.includes(option.value))
  }, [promoOptions, value])

  // Use useEffect to call promoActionList to avoid setState during render
  useEffect(() => {
    const labels = selectedOptions.map((option) => option.label)
    const previousLabels = previousLabelsRef.current
    
    // Only call promoActionList if labels actually changed
    if (JSON.stringify(labels) !== JSON.stringify(previousLabels)) {
      previousLabelsRef.current = labels
      promoActionList?.(labels)
    }
  }, [selectedOptions, promoActionList])

  // After creating a new promo action, refresh list but do NOT auto-select it
  useEffect(() => {
    if (newlyCreatedPromoId) {
      // Simply clear the flag, leaving current selection unchanged
      setNewlyCreatedPromoId(null)
    }
  }, [newlyCreatedPromoId])

  const handleChange = (
    newValue: MultiValue<PromoActionOption>,
    actionMeta: ActionMeta<PromoActionOption>
  ) => {
    const selectedValues = (newValue as PromoActionOption[]).map((option) => option.value)
    const labels = (newValue as PromoActionOption[]).map((option) => option.label)
    promoActionList?.(labels)
    onChange(selectedValues)
  }

  const customStyles: StylesConfig<PromoActionOption, true> = {
    option: (provided, state) => ({
      ...provided,
      padding: '10px 16px',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      backgroundColor: state.isDisabled
        ? '#F5F8FA'
        : state.isSelected
        ? '#f0ebff'
        : state.isFocused
        ? '#f8f5ff'
        : 'white',
      color: state.isDisabled ? '#B5B5C3' : state.isSelected ? '#7950f3' : '#3F4254',
    }),
    control: (provided, state) => ({
      ...provided,
      backgroundColor: '#f5f8fa',
      border: '1px solid #E4E6EF',
      borderRadius: '0.475rem',
      minHeight: 45,
      boxShadow: 'none',
      '&:hover': {
        border: '1px solid #E4E6EF',
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: provided.color || '#ffffff',
      borderRadius: '6px',
      padding: '2px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontSize: '13px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#7950f3',
      '&:hover': {
        backgroundColor: '#7950f3',
        color: 'white',
      },
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '8px',
      boxShadow: '0px 0px 50px 0px rgba(82, 63, 105, 0.15)',
    }),
  }

  const handleOpenPromoPopup = () => {
    setOpenPromoPopup(true)
  }

  const handleClosePromoPopup = () => {
    setOpenPromoPopup(false)
  }

  const refreshPromoActions = async () => {
    try {
      // Sort by updatedAt desc to show newest first
      const response = await getPromoActionList(DEFAULT_QUERY).unwrap()
      if (response?.data?.items) {
        setAvailablePromoActions(response.data.items)
        
        // Find the newly created item (should be first in the sorted list)
        const newestItem = response.data.items[0]
        if (newestItem?.id) {
          setNewlyCreatedPromoId(newestItem.id)
        }
      }
    } catch (error) {
      console.error('Failed to refresh promo actions:', error)
    }
  }

  const MenuList = (props: MenuListProps<PromoActionOption, true>) => {
    return (
      <div className='promo-select'>
        <div className='promo-select-content'>
          {props.children}
        </div>
        <div 
          className='promo-select-create-button d-flex align-items-center gap-2 justify-content-center cursor-pointer'
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleOpenPromoPopup()
          }}
        >
          <i className='bi bi-plus-lg text-primary fw-bold'></i>
          <p className='text-primary fw-bold'>{t('RAW_CONTACT_COLLECTION.CREATE_NEW')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className='input-promo-setting m-0 p-0'>
      <Form.Group>
        <Select<PromoActionOption, true>
          value={selectedOptions}
          components={{MenuList}}
          isMulti
          className={`select ${className}`}
          options={promoOptions}
          onChange={handleChange}
          styles={customStyles}
          isOptionDisabled={() => selectedOptions.length >= MAX_PROMO_ACTION}
          name={name}
        />
        {error && (
          <div className='fv-plugins-message-container mt-2'>
            <span role='alert' className='text-danger'>
              {error}
            </span>
          </div>
        )}
      </Form.Group>
      {openPromoPopup && (
        <CreatePromoActionModal
          show={openPromoPopup}
          handleClose={handleClosePromoPopup}
          onSuccess={() => {
            refreshPromoActions()
            handleClosePromoPopup()
          }}
        />
      )}
    </div>
  )
}

export default InputPromoAction
